import { useEffect, useState } from 'react';
import { useTask } from '@react/hooks';
import { Button, Alert, Badge } from '@react/components';
import { GetTask } from '@core/api/endpoints/task/get_task';
import type { Task } from '@core/types/task';

// Public profile IDs from infra/agents/corde-dev-registration.yaml. Passwords and
// provider credentials stay on the runner VMs, never in the frontend bundle.
const agentProfiles = new Set([
  '01a1726b-8eec-73c4-9269-f4389dc0d69c',
  '01a1726b-c558-73c4-98f3-4fc8ce6b4df4',
  '01a1726b-cd7d-73c4-bb6b-3f056c9937fa',
  '01a1726b-d4c3-73c4-83ab-eda0b413f7d4',
  '01a1726b-dd3a-73c4-a714-2fef76dc2bbd',
  '01a1726b-e58b-73c4-b1b5-3edf50571258',
  '01a1726b-edee-73c4-8231-7a76acc14557',
  '01a1726b-f546-73c4-a080-cdd897127c7b',
  '01a1726b-fd6a-73c4-8a16-d558ce98abf9',
]);

const stateLabels: Record<string, string> = {
  preparing: 'Подготовка', queued: 'В очереди', running: 'Работает', review: 'Ожидает проверки', blocked: 'Ошибка',
  stopped: 'Остановлен', revising: 'Обновление постановки', waiting_auth: 'Ожидает модель',
};

function eventLine(line: string): string {
  try {
    const event = JSON.parse(line);
    const message = event.item?.text || event.item?.message || event.message || event.error?.message;
    return message ? `${event.type || 'событие'}: ${message}` : event.type || line;
  } catch {
    return line;
  }
}

export function AgentRunPanel({ taskId, task, onChanged, onEditSpec }: {
  taskId: string;
  task: Task;
  onChanged: () => void;
  onEditSpec: () => void;
}) {
  const store = useTask();
  const [live, setLive] = useState<Task>(task);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [showLog, setShowLog] = useState(false);
  const assigneeId = task.assignee?.entity?.id;
  const isAgentTask = !!assigneeId && agentProfiles.has(assigneeId);

  useEffect(() => { setLive(task); }, [task]);
  useEffect(() => {
    if (!isAgentTask) return;
    let mounted = true;
    const poll = async () => {
      try {
        const response = await GetTask(taskId);
        if (mounted && response.task) setLive(response.task);
      } catch { /* Keep the last visible state; actions surface their own errors. */ }
    };
    const interval = window.setInterval(poll, 8000);
    return () => { mounted = false; window.clearInterval(interval); };
  }, [taskId, isAgentTask]);

  if (!isAgentTask) return null;

  const metadata = live.metadata || {};
  const running = metadata['agent.state'] === 'running';
  const stopping = running && metadata['agent.run'] === 'false';
  const active = metadata['agent.run'] === 'true' && !['review', 'blocked', 'stopped'].includes(metadata['agent.state']);
  const hasRun = !!metadata['agent.attempt'];
  const log = (metadata['agent.log_tail'] || '').trim().split('\n').filter(Boolean).map(eventLine).join('\n');

  const setRun = async (enabled: boolean) => {
    setPending(true);
    setError('');
    try {
      if (enabled && live.status !== 'TASK_STATUS_OPEN') {
        await store.ChangeTaskStatus(taskId, {
          task: live.entity,
          status: live.status === 'TASK_STATUS_IN_REVIEW' ? 'TASK_STATUS_IN_PROGRESS' : 'TASK_STATUS_OPEN',
          comment: 'Повторный запуск агента',
        });
      }
      const fresh = (await GetTask(taskId)).task || live;
      const nextMetadata = {
        ...fresh.metadata,
        'agent.run': enabled ? 'true' : 'false',
        'agent.state': enabled ? 'queued' : (running ? 'running' : 'stopped'),
        'agent.last_message': enabled ? 'Ожидает запуска на ноде' : (running ? 'Остановка запрошена' : 'Остановлен до запуска'),
      };
      await store.UpdateTask(taskId, {
        task: { ...fresh, metadata: nextMetadata },
        field_mask: ['metadata'],
      });
      const updated = (await GetTask(taskId)).task;
      if (updated) setLive(updated);
      onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось изменить выполнение');
    } finally {
      setPending(false);
    }
  };

  return (
    <section className="mt-8 border-t border-border pt-5" aria-label="Работа агента">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Работа агента</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {metadata['agent.node'] || 'Нода ещё не назначена'}
            {metadata['agent.attempt'] ? ` · попытка ${metadata['agent.attempt']}` : ''}
            {metadata['agent.updated_at'] ? ` · ${new Date(metadata['agent.updated_at']).toLocaleString('ru-RU')}` : ''}
          </p>
        </div>
        <Badge variant={running ? 'primary' : metadata['agent.state'] === 'blocked' ? 'error' : 'secondary'} size="sm">
          {stopping ? 'Останавливается' : stateLabels[metadata['agent.state']] || (metadata['agent.run'] === 'true' ? 'В очереди' : 'Не запущен')}
        </Badge>
      </div>
      {metadata['agent.last_message'] && (
        <p className="mt-3 whitespace-pre-wrap break-words text-sm text-foreground">{metadata['agent.last_message']}</p>
      )}
      {error && <div className="mt-3"><Alert variant="error">{error}</Alert></div>}
      <div className="mt-3 flex flex-wrap gap-2">
        {active ? (
          <Button size="sm" variant="secondary" isLoading={pending} onClick={() => setRun(false)}>Остановить</Button>
        ) : stopping ? null : metadata['agent.run'] !== 'true' || metadata['agent.state'] === 'blocked' || metadata['agent.state'] === 'review' || metadata['agent.state'] === 'stopped' ? (
          <Button size="sm" isLoading={pending} onClick={() => setRun(true)}>{hasRun ? 'Перезапустить' : 'Запустить'}</Button>
        ) : null}
        <Button size="sm" variant="ghost" onClick={onEditSpec}>Изменить постановку</Button>
        {log && <Button size="sm" variant="ghost" onClick={() => setShowLog(!showLog)}>{showLog ? 'Скрыть журнал' : 'Показать журнал'}</Button>}
      </div>
      {showLog && log && (
        <pre className="mt-3 max-h-64 overflow-auto rounded-md bg-muted p-3 text-xs leading-relaxed text-foreground" aria-label="Журнал агента">{log}</pre>
      )}
    </section>
  );
}
